import request from 'utils/request'
import { getStudyVersionID } from 'utils/studyConfigUtils'
import moment from 'moment'
import { MODULE_FALLBACKS } from '../utils/constants'
import { instrumentV2Urls } from '../../../../Instruments/utils/instrumentV2Api'
import { deriveScheduledItemsFromArms } from '../utils/scheduleProjection'
import {
  getFirstScheduleContentDay,
  getGridDayCount,
  GRID_DAYS_MIN,
  studyDurationFromListToDays,
} from '../utils/studyTimelineGridUtils'

export {
  deriveScheduledItemsFromArms,
  getChainDeployVisitIds,
  getInstrumentChainedStudyDay,
  getVisitChainedStudyDay,
} from '../utils/scheduleProjection'
export {
  getFirstScheduleContentDay,
  getGridDayCount,
  studyDurationFromListToDays,
} from '../utils/studyTimelineGridUtils'

//
// Action types
//

const SET_VIEW_TYPE = 'studyTimeline/SET_VIEW_TYPE'
const SET_SELECTED_ARM_ID = 'studyTimeline/SET_SELECTED_ARM_ID'
const SET_INCLUDE_VISIT_CYCLE = 'studyTimeline/SET_INCLUDE_VISIT_CYCLE'
const SET_USE_ABSOLUTE_START_DATE = 'studyTimeline/SET_USE_ABSOLUTE_START_DATE'
const SET_ABSOLUTE_START_DATE = 'studyTimeline/SET_ABSOLUTE_START_DATE'
const SET_FILTER_BY_INSTRUMENT = 'studyTimeline/SET_FILTER_BY_INSTRUMENT'
const SET_SELECTED_INSTRUMENT_ID = 'studyTimeline/SET_SELECTED_INSTRUMENT_ID'
const SET_SELECTED_DAY = 'studyTimeline/SET_SELECTED_DAY'
const SET_VISIT_SCHEDULES = 'studyTimeline/SET_VISIT_SCHEDULES'
const SET_INSTRUMENTS = 'studyTimeline/SET_INSTRUMENTS'
const SET_EXPECTED_STUDY_ID = 'studyTimeline/SET_EXPECTED_STUDY_ID'
const SET_STUDY_DURATION_FROM_LIST = 'studyTimeline/SET_STUDY_DURATION_FROM_LIST'
const RESET_STUDY_TIMELINE = 'studyTimeline/RESET_STUDY_TIMELINE'
const SET_GRID_LOADED = 'studyTimeline/SET_GRID_LOADED'

export const initialState = {
  viewType: 'overview', // 'overview' | 'scheduled'
  studyVisitSchedule: [], // Raw API response
  arms: [],
  selectedArmId: null,
  includeVisitCycle: false,
  useAbsoluteStartDate: true, // Default to true
  absoluteStartDate: moment().format('YYYY-MM-DD'), // Default to today's date
  filterByInstrument: false,
  instruments: [],
  selectedInstrumentId: null,
  days: Array.from({ length: GRID_DAYS_MIN }, (_, index) => index + 1),
  scheduledItems: [], // Active cycle ranges for overlay visualization
  cycleRanges: [],
  selectedDay: 1,
  gridLoaded: false, // Track if Go button has been clicked to load the grid
  expectedStudyId: null, // Ignore API responses for a different study (avoids stale cache)
  studyDurationFromList: null, // From studies list (study card click) for grid/scheduled view day count
}

//
// Reducer
//

export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case SET_VIEW_TYPE:
      return {
        ...state,
        viewType: action.payload,
      }
    case SET_SELECTED_ARM_ID:
      const armDerived = deriveScheduledItemsFromArms(
        state.arms,
        action.payload,
        state.instruments,
        state.useAbsoluteStartDate,
        state.absoluteStartDate,
        state.includeVisitCycle,
        studyDurationFromListToDays(state.studyDurationFromList),
      )
      // Keep same grid length when switching arms (study duration does not change; only cycle/visits do)
      const currentDayCount = state.days && state.days.length > 0 ? state.days.length : null
      const armDays =
        currentDayCount != null
          ? currentDayCount
          : getGridDayCount(
            armDerived.items || [],
            armDerived.cycleRanges || [],
            studyDurationFromListToDays(state.studyDurationFromList),
          )
      return {
        ...state,
        selectedArmId: action.payload,
        scheduledItems: armDerived.items || [],
        cycleRanges: armDerived.cycleRanges || [],
        days: Array.from({ length: armDays }, (_, i) => i + 1),
        selectedDay: getFirstScheduleContentDay(armDerived.items || [], armDerived.cycleRanges || []) ?? state.selectedDay ?? 1,
        gridLoaded: false,
      }
    case SET_INCLUDE_VISIT_CYCLE: {
      const newIncludeVisitCycle = action.payload
      // When Include Visit Cycle is enabled, the first arm in the list must be pre-selected
      const firstArmId = state.arms?.length > 0 && state.arms[0]?.id != null ? String(state.arms[0].id) : null
      const effectiveArmId =
        newIncludeVisitCycle && firstArmId != null && (state.selectedArmId == null || state.selectedArmId === '')
          ? firstArmId
          : state.selectedArmId
      const derivedIncludeCycle = deriveScheduledItemsFromArms(
        state.arms,
        effectiveArmId,
        state.instruments,
        state.useAbsoluteStartDate,
        state.absoluteStartDate,
        newIncludeVisitCycle,
        studyDurationFromListToDays(state.studyDurationFromList),
      )
      // Keep existing grid day count unchanged when toggling visit cycle (study duration / grid days do not change)
      const currentDayCount = state.days && state.days.length > 0 ? state.days.length : GRID_DAYS_MIN
      return {
        ...state,
        includeVisitCycle: newIncludeVisitCycle,
        ...(effectiveArmId !== state.selectedArmId ? { selectedArmId: effectiveArmId } : {}),
        scheduledItems: derivedIncludeCycle.items || [],
        cycleRanges: derivedIncludeCycle.cycleRanges || [],
        days: Array.from({ length: currentDayCount }, (_, i) => i + 1),
        selectedDay: getFirstScheduleContentDay(derivedIncludeCycle.items || [], derivedIncludeCycle.cycleRanges || []) ?? state.selectedDay ?? 1,
        gridLoaded: false,
      }
    }
    case SET_USE_ABSOLUTE_START_DATE:
      const newUseAbsolute = action.payload
      // Re-derive scheduled items when absolute date toggle changes
      const derivedOnToggle = deriveScheduledItemsFromArms(
        state.arms,
        state.selectedArmId,
        state.instruments,
        newUseAbsolute,
        newUseAbsolute ? state.absoluteStartDate : null,
        state.includeVisitCycle,
        studyDurationFromListToDays(state.studyDurationFromList),
      )
      const toggleDays = getGridDayCount(
        derivedOnToggle.items || [],
        derivedOnToggle.cycleRanges || [],
        studyDurationFromListToDays(state.studyDurationFromList),
      )
      return {
        ...state,
        useAbsoluteStartDate: newUseAbsolute,
        absoluteStartDate: newUseAbsolute ? state.absoluteStartDate : null,
        scheduledItems: derivedOnToggle.items || [],
        cycleRanges: derivedOnToggle.cycleRanges || [],
        days: Array.from({ length: toggleDays }, (_, i) => i + 1),
        selectedDay: getFirstScheduleContentDay(derivedOnToggle.items || [], derivedOnToggle.cycleRanges || []) ?? state.selectedDay ?? 1,
        gridLoaded: false,
      }
    case SET_ABSOLUTE_START_DATE:
      // Re-derive scheduled items when absolute start date changes
      const derivedOnDateChange = deriveScheduledItemsFromArms(
        state.arms,
        state.selectedArmId,
        state.instruments,
        state.useAbsoluteStartDate,
        action.payload,
        state.includeVisitCycle,
        studyDurationFromListToDays(state.studyDurationFromList),
      )
      const dateChangeDays = getGridDayCount(
        derivedOnDateChange.items || [],
        derivedOnDateChange.cycleRanges || [],
        studyDurationFromListToDays(state.studyDurationFromList),
      )
      return {
        ...state,
        absoluteStartDate: action.payload,
        scheduledItems: derivedOnDateChange.items || [],
        cycleRanges: derivedOnDateChange.cycleRanges || [],
        days: Array.from({ length: dateChangeDays }, (_, i) => i + 1),
        selectedDay: getFirstScheduleContentDay(derivedOnDateChange.items || [], derivedOnDateChange.cycleRanges || []) ?? state.selectedDay ?? 1,
        gridLoaded: false,
      }
    case SET_FILTER_BY_INSTRUMENT:
      return {
        ...state,
        filterByInstrument: action.payload,
        selectedInstrumentId: action.payload ? state.selectedInstrumentId : null,
      }
    case SET_SELECTED_INSTRUMENT_ID:
      return {
        ...state,
        selectedInstrumentId: action.payload,
      }
    case SET_SELECTED_DAY:
      return {
        ...state,
        selectedDay: action.payload,
      }
    case SET_EXPECTED_STUDY_ID:
      return {
        ...state,
        expectedStudyId: action.payload != null ? action.payload : null,
      }
    case SET_STUDY_DURATION_FROM_LIST: {
      const minDays = studyDurationFromListToDays(action.payload)
      const newDayCount = getGridDayCount(
        state.scheduledItems || [],
        state.cycleRanges || [],
        minDays,
      )
      return {
        ...state,
        studyDurationFromList: action.payload,
        days: Array.from({ length: newDayCount }, (_, i) => i + 1),
      }
    }
    case SET_VISIT_SCHEDULES: {
      // Ignore stale response if user switched to another study before this response arrived
      const payload = action.payload
      const isNewShape = payload && typeof payload === 'object' && !Array.isArray(payload) && 'studyId' in payload
      const studyId = isNewShape ? payload.studyId : null
      const data = isNewShape ? payload.data : payload
      const expectedId = state.expectedStudyId != null ? state.expectedStudyId : null
      if (studyId != null && expectedId != null && String(studyId) !== String(expectedId)) {
        return state
      }
      // Use study_duration from payload (API or currentStudy fallback in thunk) so grid shows full duration after refresh
      const rawDuration = payload?.study_duration
      const nextStudyDurationFromList =
        rawDuration != null
          ? (typeof rawDuration === 'object' && rawDuration.study_duration != null
              ? rawDuration
              : { study_duration: rawDuration })
          : state.studyDurationFromList
      const studyVisitSchedule = Array.isArray(data) ? data : (Array.isArray(payload) ? payload : [])
      const arms = studyVisitSchedule.map(arm => ({
        id: arm.id,
        name: arm.name,
        ...arm,
      }))
      const firstArmId = arms.length > 0 ? (arms[0].id ? String(arms[0].id) : null) : null
      const newSelectedArmId = firstArmId || state.selectedArmId
      const derived = deriveScheduledItemsFromArms(
        arms,
        newSelectedArmId,
        state.instruments || [],
        state.useAbsoluteStartDate,
        state.absoluteStartDate,
        state.includeVisitCycle,
        studyDurationFromListToDays(nextStudyDurationFromList),
      )
      const minDaysFromList = studyDurationFromListToDays(nextStudyDurationFromList)
      const visitScheduleDays = getGridDayCount(
        derived.items || [],
        derived.cycleRanges || [],
        minDaysFromList,
      )
      return {
        ...state,
        studyVisitSchedule,
        arms,
        selectedArmId: newSelectedArmId,
        scheduledItems: derived.items || [],
        cycleRanges: derived.cycleRanges || [],
        studyDurationFromList: nextStudyDurationFromList,
        days: Array.from({ length: visitScheduleDays }, (_, i) => i + 1),
        selectedDay: getFirstScheduleContentDay(derived.items || [], derived.cycleRanges || []) ?? state.selectedDay ?? 1,
      }
    }
    case SET_INSTRUMENTS: {
      const payload = action.payload
      const isNewShape = payload && typeof payload === 'object' && !Array.isArray(payload) && 'studyId' in payload
      const instrumentsStudyId = isNewShape ? payload.studyId : null
      const instrumentsPayload = isNewShape ? payload.data : payload
      const expectedIdInstruments = state.expectedStudyId != null ? state.expectedStudyId : null
      if (instrumentsStudyId != null && expectedIdInstruments != null && String(instrumentsStudyId) !== String(expectedIdInstruments)) {
        return state
      }
      const instrumentsList = Array.isArray(instrumentsPayload) ? instrumentsPayload : []
      // Store full instrument objects for schedule processing, but also create simplified version for dropdown
      const instruments = instrumentsList.map(instrument => ({
        id: instrument.id,
        name:
          instrument.metadata?.display_name ||
          instrument.title ||
          instrument.name ||
          `${MODULE_FALLBACKS.INSTRUMENT_NAME_PREFIX}${instrument.id}`,
        // Keep full instrument data for schedule processing
        ...instrument,
      }))
      // Re-derive scheduled items with instruments if arms are already loaded
      if (state.arms && state.arms.length > 0 && state.selectedArmId) {
        const derivedWithInstruments = deriveScheduledItemsFromArms(
          state.arms,
          state.selectedArmId,
          instruments,
          state.useAbsoluteStartDate,
          state.absoluteStartDate,
          state.includeVisitCycle,
          studyDurationFromListToDays(state.studyDurationFromList),
        )
        const instrumentsDays = getGridDayCount(
          derivedWithInstruments.items || [],
          derivedWithInstruments.cycleRanges || [],
          studyDurationFromListToDays(state.studyDurationFromList),
        )
        return {
          ...state,
          instruments,
          scheduledItems: derivedWithInstruments.items || [],
          cycleRanges: derivedWithInstruments.cycleRanges || [],
          days: Array.from({ length: instrumentsDays }, (_, i) => i + 1),
          selectedDay: getFirstScheduleContentDay(derivedWithInstruments.items || [], derivedWithInstruments.cycleRanges || []) ?? state.selectedDay ?? 1,
        }
      }
      if (state.arms && state.arms.length === 0) {
        const derivedNoArms = deriveScheduledItemsFromArms(
          [],
          null,
          instruments,
          state.useAbsoluteStartDate,
          state.absoluteStartDate,
          state.includeVisitCycle,
          studyDurationFromListToDays(state.studyDurationFromList),
        )
        const noArmsDays = getGridDayCount(
          derivedNoArms.items || [],
          derivedNoArms.cycleRanges || [],
          studyDurationFromListToDays(state.studyDurationFromList),
        )
        return {
          ...state,
          instruments,
          scheduledItems: derivedNoArms.items || [],
          cycleRanges: derivedNoArms.cycleRanges || [],
          days: Array.from({ length: noArmsDays }, (_, i) => i + 1),
          selectedDay: getFirstScheduleContentDay(derivedNoArms.items || [], derivedNoArms.cycleRanges || []) ?? state.selectedDay ?? 1,
        }
      }
      // If arms not loaded yet, just store instruments (they'll be included when SET_VISIT_SCHEDULES is dispatched)
      return {
        ...state,
        instruments,
      }
    }
    case SET_GRID_LOADED:
      return {
        ...state,
        gridLoaded: action.payload,
      }
    case RESET_STUDY_TIMELINE:
      return {
        ...initialState,
      }
    default:
      return state
  }
}

//
// Action creators
//

const setViewType = payload => ({
  type: SET_VIEW_TYPE,
  payload,
})

const setSelectedArmId = payload => ({
  type: SET_SELECTED_ARM_ID,
  payload,
})

const setIncludeVisitCycle = payload => ({
  type: SET_INCLUDE_VISIT_CYCLE,
  payload,
})

const setUseAbsoluteStartDate = payload => ({
  type: SET_USE_ABSOLUTE_START_DATE,
  payload,
})

const setAbsoluteStartDate = payload => ({
  type: SET_ABSOLUTE_START_DATE,
  payload,
})

const setFilterByInstrument = payload => ({
  type: SET_FILTER_BY_INSTRUMENT,
  payload,
})

const setSelectedInstrumentId = payload => ({
  type: SET_SELECTED_INSTRUMENT_ID,
  payload,
})

const setSelectedDay = payload => ({
  type: SET_SELECTED_DAY,
  payload,
})

const setGridLoaded = payload => ({
  type: SET_GRID_LOADED,
  payload,
})

const setExpectedStudyId = payload => ({
  type: SET_EXPECTED_STUDY_ID,
  payload,
})

export const setStudyDurationFromList = payload => ({
  type: SET_STUDY_DURATION_FROM_LIST,
  payload,
})

const setVisitSchedules = (data, studyId, studyDuration = null) => ({
  type: SET_VISIT_SCHEDULES,
  payload: { data, studyId, study_duration: studyDuration },
})

const setInstruments = (data, studyId) => ({
  type: SET_INSTRUMENTS,
  payload: { data, studyId },
})

const resetStudyTimeline = () => ({
  type: RESET_STUDY_TIMELINE,
})

// Thunk to fetch visit schedules
const fetchVisitSchedules = studyId => (dispatch, getState) => {
  const { study, location } = getState()
  const hasStudyPromotion = !!study?.currentStudy?.config?.study_promotion
  const studyVersionID = getStudyVersionID(location)

  const getQueryParams = () => {
    if (hasStudyPromotion) {
      return `?study_version_id=${studyVersionID}`
    }
    return ''
  }

  const safeStudyId = studyId != null ? studyId : null
  dispatch(setExpectedStudyId(safeStudyId))
  const success = payload => {
    const data = payload.study_visit_schedule || []
    dispatch(setVisitSchedules(data, safeStudyId))
  }

  return dispatch(
    request({
      url: `/control/studies/${safeStudyId}/study_schedules${getQueryParams()}`,
      success,
      hasLoader: true,
      loadingKey: 'studyTimeline',
    }),
  )
}

// Thunk to fetch instruments (V2 list so metadata.schedule arrays are returned)
const fetchInstruments = studyId => (dispatch, getState) => {
  const { study, location } = getState()
  const hasStudyPromotion = !!study?.currentStudy?.config?.study_promotion
  const studyVersionID = getStudyVersionID(location)

  const getQueryParams = () => {
    const params = ['include_compliance=false']
    if (hasStudyPromotion) {
      params.push(`study_version_id=${studyVersionID}`)
    }
    return `?${params.join('&')}`
  }

  const safeStudyId = studyId != null ? studyId : null
  const success = payload => {
    const instruments = payload?.results || []
    dispatch(setInstruments(instruments, safeStudyId))
  }

  return dispatch(
    request({
      url: instrumentV2Urls.list(safeStudyId, getQueryParams()),
      success,
      hasLoader: true,
      loadingKey: 'studyTimelineInstruments',
    }),
  )
}

export const actions = {
  setViewType,
  setSelectedArmId,
  setIncludeVisitCycle,
  setUseAbsoluteStartDate,
  setAbsoluteStartDate,
  setFilterByInstrument,
  setSelectedInstrumentId,
  setSelectedDay,
  setGridLoaded,
  setStudyDurationFromList,
  fetchVisitSchedules,
  fetchInstruments,
  resetStudyTimeline,
}

